import { useQueryClient } from 'react-query'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum, ZapierIntegration } from 'api/clients/ExternalIntegrationsClient'
import { ZapierTargetsClient } from 'api/clients/ZapierTargetsClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ZapierLogo } from 'almanac-ui/icons/24'
import ArrowRefresh from 'blaze-ui/icons/20/ArrowRefresh'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useModals } from 'almanac-ui/Modal'
import { ConfirmModal } from 'blaze-components/ConfirmModal/ConfirmModal'
import { PLATFORM_DISPLAY_NAMES } from 'utils/platformDisplayNames'
import Styles from './ZapierTargetsPicker.module.scss'

const BLOG_PROVIDERS: ExternalIntegrationEnum[] = [
  ExternalIntegrationEnum.WordPress,
  ExternalIntegrationEnum.GoHighLevel,
  ExternalIntegrationEnum.Wix,
  ExternalIntegrationEnum.Shopify,
]

const EMAIL_PROVIDERS: ExternalIntegrationEnum[] = [ExternalIntegrationEnum.Mailchimp]

// Lets a user mark which subscribed Zap is the workspace's active blog or
// newsletter destination. One Zap per channel — toggling on this row unsets
// the same channel on every other Zap (enforced server-side).
export const ZapierTargetsPicker = () => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { openModal } = useModals()
  const { data, isFetched } = useExternalIntegrations()

  const allIntegrations = data?.external_integrations ?? []
  const zaps = allIntegrations
    .filter((i): i is ZapierIntegration => i.provider === ExternalIntegrationEnum.Zapier)
    // Sort by id so toggling a row (which bumps updated_at and may re-sort the
    // backend response) doesn't reshuffle the visible list.
    .sort((a, b) => a.id - b.id)

  const existingPlatformForChannel = (channel: 'is_blog_destination' | 'is_email_destination') => {
    const providers = channel === 'is_blog_destination' ? BLOG_PROVIDERS : EMAIL_PROVIDERS
    return allIntegrations.find((i) => providers.includes(i.provider))
  }

  const commitToggle = async (
    integration: ZapierIntegration,
    channel: 'is_blog_destination' | 'is_email_destination',
    nextValue: boolean,
  ) => {
    const res = await ZapierTargetsClient.setDestinationFlags(workspaceId, integration.id, {
      [channel]: nextValue,
    })

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      // Bust caches for everything that derives the active blog/email platform:
      // - integrations list (the Zap row's flags + any destroyed legacy platform)
      // - brand plan (selected_blog_platform / selected_email_platform updated server-side)
      // - scheduled posts (FKs may have been reassigned via the transfer)
      // - files lists (icons in /all_files re-render based on the resolved platform)
      queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandPlans'])
      queryClient.invalidateQueries(['scheduledPosts'])
      queryClient.invalidateQueries(['files'])
      showToastMessage(
        nextValue
          ? `${integration.name} set as ${channel === 'is_blog_destination' ? 'blog' : 'newsletter'} destination`
          : `${integration.name} unset as ${channel === 'is_blog_destination' ? 'blog' : 'newsletter'} destination`,
        'success',
      )
    } else {
      showErrorMessageToast('Failed to update Zap destination')
    }
  }

  const toggle = (
    integration: ZapierIntegration,
    channel: 'is_blog_destination' | 'is_email_destination',
    nextValue: boolean,
  ) => {
    // When turning a Zap ON as a destination AND the workspace already has a
    // native blog/email provider connected, confirm before the switch — the
    // backend will migrate scheduled posts to the Zap and DESTROY the existing
    // integration (matching the WordPress↔Wix switch behavior).
    if (nextValue) {
      const existing = existingPlatformForChannel(channel)
      if (existing) {
        const displayName = PLATFORM_DISPLAY_NAMES[existing.provider] || existing.provider
        const channelLabel = channel === 'is_blog_destination' ? 'blog' : 'newsletter'
        openModal(ConfirmModal, {
          size: 'sm',
          title: `Switch ${channelLabel} destination to Zapier?`,
          cancelText: 'Cancel',
          confirmText: `Yes, replace ${displayName}`,
          prompt: `Your existing ${displayName} integration will be removed and all scheduled ${channelLabel} posts will be moved to this Zap. This action cannot be undone.`,
          onConfirm: () => commitToggle(integration, channel, true),
        })
        return
      }
    }

    commitToggle(integration, channel, nextValue)
  }

  const refreshZaps = () => {
    queryClient.invalidateQueries(['externalIntegrations', workspaceId])
    showToastMessage('Checking for Zaps...', 'success')
  }

  if (!isFetched) return null

  // No Zapier integrations yet — the existing "Use the API key below to connect
  // with Zapier" section in ZapierIntegration handles this empty state. We
  // hide the picker entirely so users see one clear setup CTA, not two.
  if (zaps.length === 0) return null

  return (
    <div className={Styles.container}>
      <div className={Styles.header}>
        <Heading level={5}>Your Connected Zaps</Heading>
        <Button variant="secondary" size="sm" frontIcon={ArrowRefresh} onPress={refreshZaps}>
          Refresh
        </Button>
      </div>
      <Text variant="secondary">
        Mark a Zap as your blog or newsletter destination. Scheduled posts of that type will route to that Zap. Only one
        Zap can serve each destination at a time.
      </Text>
      <ul className={Styles.list}>
        {zaps.map((zap) => (
          <li key={zap.id} className={Styles.row}>
            <div className={Styles.zapInfo}>
              <ZapierLogo size={24} />
              <Text variant="primary">{zap.name}</Text>
            </div>
            <div className={Styles.toggles}>
              <Checkbox
                isSelected={!!zap.additional_data?.is_blog_destination}
                onChange={(checked) => toggle(zap, 'is_blog_destination', checked)}
              >
                Use for blog posts
              </Checkbox>
              <Checkbox
                isSelected={!!zap.additional_data?.is_email_destination}
                onChange={(checked) => toggle(zap, 'is_email_destination', checked)}
              >
                Use for newsletters
              </Checkbox>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
